/**
 * Hook: per-section LLM feedback for a simulation module.
 *
 * Fetches `tb_section_feedback` rows for one (session, module) and polls every
 * 10s while any section is still `pending`.
 *
 * @module useSectionFeedback
 */
import { useCallback, useEffect, useRef, useState } from 'react';

import {
  getModuleSectionFeedback,
  type ApiSectionFeedback,
} from '@/lib/simulationResultsApi';

const POLL_INTERVAL_MS = 10_000;
const SECTION_FEEDBACK_MODULES = new Set(['reading', 'listening', 'writing', 'speaking']);

export interface UseSectionFeedbackReturn {
  readonly feedback: ApiSectionFeedback[];
  readonly isLoading: boolean;
  readonly refetch: () => Promise<void>;
}

export function useSectionFeedback(
  sessionId: string | undefined | null,
  module: string | undefined | null,
): UseSectionFeedbackReturn {
  const [feedback, setFeedback] = useState<ApiSectionFeedback[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const mountedRef = useRef(true);

  const moduleLower = (module ?? '').toLowerCase();
  const supportsSectionFeedback = SECTION_FEEDBACK_MODULES.has(moduleLower);

  const fetchOnce = useCallback(async () => {
    if (!sessionId || !supportsSectionFeedback) {
      setFeedback([]);
      setIsLoading(false);
      return;
    }
    const rows = await getModuleSectionFeedback(sessionId, moduleLower);
    if (!mountedRef.current) return;
    setFeedback(rows);
    setIsLoading(false);
  }, [sessionId, moduleLower, supportsSectionFeedback]);

  // Initial fetch when (session, module) changes.
  useEffect(() => {
    mountedRef.current = true;
    setIsLoading(true);
    void fetchOnce();
    return () => {
      mountedRef.current = false;
    };
  }, [fetchOnce]);

  // Poll while anything is pending.
  useEffect(() => {
    if (!supportsSectionFeedback) return;
    const hasPending = feedback.some((row) => row.status === 'pending');
    if (!hasPending) return;
    const handle = setInterval(() => {
      void fetchOnce();
    }, POLL_INTERVAL_MS);
    return () => clearInterval(handle);
  }, [feedback, fetchOnce, supportsSectionFeedback]);

  return { feedback, isLoading, refetch: fetchOnce };
}
